import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import { useSubscription } from "shared/contexts/Subscription";
import { mockEvaluations } from "shared/services/mockData";
import apiCall from "shared/services/ApiAdapter";
import ContentHeaderSection from "shared/common/ContentHeaderSection";
import { useMasterList } from "shared/hooks/useMasterList";
import masterApiCall from "shared/services/MasterDataApi";

const CreateUpdateBatchEvaluationForm = ({
  navigateTo,
  evaluationId,
  selectedBatchId,
  useMockData,
  displayToast,
}) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const { canPerformAction } = useSubscription();

  const [formLoading, setFormLoading] = useState(false);
  const [evaluation, setEvaluation] = useState({
    chapterCode: "",
    conceptTopic: "",
    difficulty: "",
    sourceCode: "",
    numberOfQuestions: "",
    maxTimeMinutes: "",
    expiryDateTime: "",
    isPractice: false,
    isActive: true,
    tags: [],
    batchId: selectedBatchId,
  });

  // Master data for resolving codes
  const { masterData, isLoading: masterDataLoading } = useMasterList({
    useMockData,
    displayToast,
    startLoading,
    stopLoading,
    apiCall: masterApiCall,
  });

  const chapters = useMemo(() => masterData?.chapters || [], [masterData]);
  const tags = useMemo(() => masterData?.tags || [], [masterData]);

  // When editing, map existing evaluation chapter/name to code using master data
  useEffect(() => {
    if (!evaluationId) return;
    if (masterDataLoading) return;
    setEvaluation((prev) => {
      // If chapterCode already present, keep it; else try resolving from any present chapter/name
      if (prev.chapterCode) return prev;
      const chapterName = prev.chapter || prev.chapterName || "";
      if (!chapterName) return prev;
      const match = chapters.find(
        (c) => c.label === chapterName || c.name === chapterName
      );
      return match ? { ...prev, chapterCode: match.value } : prev;
    });
  }, [evaluationId, masterDataLoading, chapters]);

  useEffect(() => {
    const fetchEvaluationDetails = async () => {
      if (evaluationId) {
        setFormLoading(true);
        try {
          // Get the batch first to find its course
          const batch = await apiCall(
            `/batches/${selectedBatchId}`,
            useMockData,
            "GET",
            null,
            startLoading,
            stopLoading,
            displayToast
          );

          if (batch && batch.courseId) {
            const data = await apiCall(
              `/courses/${batch.courseId}/evaluations/${evaluationId}`,
              useMockData,
              "GET",
              null,
              startLoading,
              stopLoading,
              displayToast
            );
            if (data) {
              // Map incoming data to new shape (codes where possible)
              const chapterName = data.chapter || data.chapterName || "";
              const chapterMatch = chapters.find(
                (c) => c.label === chapterName || c.name === chapterName
              );
              const tagsArray = typeof data.tagIds === "string"
                ? data.tagIds.split(",").map((s) => s.trim()).filter(Boolean)
                : Array.isArray(data.tagIds)
                ? data.tagIds.map((v) => String(v))
                : [];
              setEvaluation({
                chapterCode: chapterMatch?.value || data.chapterCode || "",
                conceptTopic: data.conceptTopic || "",
                difficulty: data.difficulty || "",
                sourceCode: data.sourceCode || data.source || "",
                numberOfQuestions: data.numberOfQuestions ?? "",
                maxTimeMinutes: data.maxTimeMinutes ?? "",
                expiryDateTime: data.expiryDateTime?.split("T")[0] ?? "",
                isPractice: !!data.isPractice,
                isActive: data.isActive !== false,
                tags: tagsArray,
                batchId: selectedBatchId,
              });
            } else {
              displayToast(
                `Evaluation with ID ${evaluationId} not found.`,
                "error"
              );
            }
          }
        } catch (err) {
          console.error("Failed to fetch evaluation:", err);
          displayToast(`Error: ${err.message}`, "error");
        } finally {
          setFormLoading(false);
        }
      }
    };
    fetchEvaluationDetails();
  }, [evaluationId, selectedBatchId, useMockData, chapters]);

  const handleChange = (e) => {
    const { name, value, type, checked, multiple, options } = e.target;
    if (multiple) {
      const selectedValues = Array.from(options)
        .filter((opt) => opt.selected)
        .map((opt) => opt.value);
      setEvaluation((prev) => ({ ...prev, [name]: selectedValues }));
      return;
    }
    setEvaluation((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    if (
      !evaluationId &&
      !canPerformAction("maxEvaluationsPerBatch", mockEvaluations.length)
    ) {
      displayToast(t("limitReached"), "error");
      setFormLoading(false);
      return;
    }

    // Resolve classCode from the course tied to the batch
    let classCode = "";
    try {
      const batch = await apiCall(
        `/batches/${selectedBatchId}`,
        useMockData,
        "GET",
        null,
        startLoading,
        stopLoading,
        displayToast
      );
      if (batch?.courseId) {
        const course = await apiCall(
          `/courses/${batch.courseId}`,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );
        classCode = course?.academicClass || "";
      }
    } catch (e) {
      console.warn("Could not resolve classCode for evaluation payload:", e);
    }

    const payload = {
      chapterCode: evaluation.chapterCode || "",
      classCode,
      sourceCode: evaluation.sourceCode || "",
      conceptTopic: evaluation.conceptTopic || "",
      difficulty: evaluation.difficulty || "",
      numberOfQuestions: parseInt(evaluation.numberOfQuestions),
      maxTimeMinutes: parseInt(evaluation.maxTimeMinutes),
      expiryDateTime: evaluation.expiryDateTime
        ? new Date(evaluation.expiryDateTime).toISOString()
        : null,
      isPractice: !!evaluation.isPractice,
      isActive: evaluation.isActive !== false,
      ...(Array.isArray(evaluation.tags) && evaluation.tags.length > 0
        ? { tagIds: evaluation.tags.join(",") }
        : {}),
      batchId: selectedBatchId,
    };

    try {
      // Get the batch first to find its course
      const batch = await apiCall(
        `/batches/${selectedBatchId}`,
        useMockData,
        "GET",
        null,
        startLoading,
        stopLoading,
        displayToast
      );

      if (batch && batch.courseId) {
        if (evaluationId) {
          await apiCall(
            `/courses/${batch.courseId}/evaluations/${evaluationId}`,
            useMockData,
            "PUT",
            { ...payload, id: evaluationId },
            startLoading,
            stopLoading,
            displayToast
          );
          displayToast(t("evaluationUpdatedSuccessfully"), "success");
        } else {
          await apiCall(
            `/courses/${batch.courseId}/evaluations`,
            useMockData,
            "POST",
            payload,
            startLoading,
            stopLoading,
            displayToast
          );
          displayToast(t("evaluationCreatedSuccessfully"), "success");
        }
        navigateTo("batchEvaluations", "", "", selectedBatchId);
      } else {
        displayToast("Batch not found or has no associated course", "error");
      }
    } catch (err) {
      console.error("Failed to save evaluation:", err);
      displayToast(`Error: ${err.message}`, "error");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateTo("batchEvaluations", selectedBatchId)}
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
              aria-label="Go back to batch evaluations"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
              {evaluationId ? t("updateEvaluation") : t("createEvaluation")}
            </span>
          </div>
        }
        subtitle={t("createOrUpdateEvaluation")}
      />
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
        {formLoading ? (
          <div className="text-center text-gray-600 dark:text-gray-400">
            {t("loadingEvaluationDetails")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block mb-1 font-medium">{t("Chapter")}</label>
              <select
                name="chapterCode"
                value={evaluation.chapterCode}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t("select")}</option>
                {chapters.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-1 font-medium">
                {t("Concept Topic")}
              </label>
              <input
                type="text"
                name="conceptTopic"
                value={evaluation.conceptTopic}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">
                {t("Difficulty Level")}
              </label>
              <select
                name="difficulty"
                value={evaluation.difficulty}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t("select")}</option>
                {["LOW", "MED", "HIGH"].map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-1 font-medium">
                {t("Source Code")}
              </label>
              <input
                type="text"
                name="sourceCode"
                value={evaluation.sourceCode}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">{t("Tags")}</label>
              <select
                name="tags"
                multiple
                value={evaluation.tags}
                onChange={handleChange}
                disabled={masterDataLoading}
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 dark:text-white min-h-[120px]"
              >
                {tags.map((tg) => (
                  <option key={tg.value} value={tg.value}>
                    {tg.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t("Hold Ctrl/Cmd to select multiple tags")}
              </p>
            </div>

            <div>
              <label className="block mb-1 font-medium">
                {t("Number of Questions")}
              </label>
              <input
                type="number"
                name="numberOfQuestions"
                value={evaluation.numberOfQuestions}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">
                {t("Max Time (minutes)")}
              </label>
              <input
                type="number"
                name="maxTimeMinutes"
                value={evaluation.maxTimeMinutes}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">
                {t("Expiry Date")}
              </label>
              <input
                type="date"
                name="expiryDateTime"
                value={evaluation.expiryDateTime}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Duration seconds removed per codes-based payload requirements */}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPractice"
                name="isPractice"
                checked={evaluation.isPractice}
                onChange={handleChange}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="isPractice"
                className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
              >
                {t("Is Practice Evaluation")}
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={evaluation.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="isActive"
                className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
              >
                {t("Active")}
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => navigateTo("batchEvaluations", selectedBatchId)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={formLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
              >
                {formLoading
                  ? t("saving")
                  : t(evaluationId ? "Update" : "Create")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateUpdateBatchEvaluationForm;
